import React, { Suspense, lazy, useEffect } from 'react';
import Layout from './components/Layout';
import About from './components/About/About';
import NotFound from './components/NotFound';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import './style.scss';
import { I18nextProvider } from 'react-i18next';
import SEO from './components/Seo';
import ConsentBanner from './components/ConsentBanner';
import i18n from './i18n';
import { PREFIXED_LANGUAGES, DEFAULT_LANGUAGE, getLanguageFromPath, localizePath, consumeLanguageIntent } from './i18n/languages';

const Home = lazy(() => import('./components/Home'));
const CountryFullFinInfo = lazy(() => import('./components/FullFinInfo/CountryFullFinInfo'));
const EconomicIndicatorsMap = lazy(() => import('./components/EconomicIndicatorsMap'));
const Compare = lazy(() => import('./components/Compare'));
const ForecastAccuracy = lazy(() => import('./components/ForecastAccuracy'));
const CorrelationExplorer = lazy(() => import('./components/CorrelationExplorer'));
const UnderstandingGdpGuide = lazy(() => import('./components/guides/UnderstandingGdpGuide'));
const UnderstandingInflationGuide = lazy(() => import('./components/guides/UnderstandingInflationGuide'));
const CountryComparisonGuide = lazy(() => import('./components/guides/CountryComparisonGuide'));

// Route table shared by the English root and every language prefix.
// English lives at /, other languages at /:lng/... - each version gets its
// own indexable URL.
const appRoutes = [
  { path: '/', element: <Home />, index: true },
  { path: 'map', element: <EconomicIndicatorsMap /> },
  { path: 'compare', element: <Compare /> },
  { path: 'forecast-accuracy', element: <ForecastAccuracy /> },
  { path: 'countries/:code', element: <CountryFullFinInfo /> },
  { path: 'About', element: <About /> },
  { path: 'correlation', element: <CorrelationExplorer /> },
  { path: 'guides/understanding-gdp', element: <UnderstandingGdpGuide /> },
  { path: 'guides/understanding-inflation', element: <UnderstandingInflationGuide /> },
  { path: 'guides/country-comparison-methodology', element: <CountryComparisonGuide /> },
];

const renderRoute = (route) =>
  route.index
    ? <Route key="index" index element={route.element} />
    : <Route key={route.path} path={route.path} element={route.element} />;

// Keeps i18n language and the URL in sync. The URL prefix is the source of
// truth; unprefixed internal links redirect to the localized URL so the
// active language survives navigation. An explicit switch to the default
// (unprefixed) language is honored via the language intent flag.
const LanguageSync = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const routeLang = getLanguageFromPath(location.pathname) || DEFAULT_LANGUAGE;
    const intent = consumeLanguageIntent();

    // An unprefixed URL while a non-English language is active means an
    // internal link without a language prefix was followed - redirect to the
    // localized URL so the user stays in their language.
    if (routeLang === DEFAULT_LANGUAGE && !intent && i18n.language !== DEFAULT_LANGUAGE) {
      navigate(localizePath(location.pathname, i18n.language), { replace: true });
      return;
    }

    const targetLanguage = intent || routeLang;
    if (targetLanguage !== i18n.language) {
      i18n.changeLanguage(targetLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return null;
};

const App = () => (
  <>
    <SEO
      author="Anton Nalyvaiko"
    />

    <React.StrictMode>
      <Suspense fallback={<div className="my-spinner">Loading...</div>}>
        <I18nextProvider i18n={i18n}>
          <LanguageSync />
          <Layout>
            <Routes>
              {appRoutes.map(renderRoute)}

              {PREFIXED_LANGUAGES.map((lng) => (
                <Route key={lng} path={`/${lng}`}>
                  {appRoutes.map(renderRoute)}
                  <Route path="*" element={<NotFound />} />
                </Route>
              ))}

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Layout>
        </I18nextProvider>
      </Suspense>
    </React.StrictMode>
    <ConsentBanner />
  </>
);

export default App;
